import { useCallback, useEffect, useState } from 'react';
import { Alert, Box, Button, Checkbox, FormControlLabel, Typography } from '@mui/material';

export type TeacherRequest = <T>(
  method: 'GET' | 'POST' | 'DELETE',
  path: string,
  body?: unknown,
) => Promise<T>;
type Invitation = { id: string; instructorName: string; message?: string; expiresAt: string };
type Membership = { id: string; instructorName: string; acceptedAt: string };

export default function TeacherConnections({
  request,
  onChange,
}: {
  request: TeacherRequest;
  onChange: () => void;
}) {
  const [invitations, setInvitations] = useState<Invitation[]>([]);
  const [memberships, setMemberships] = useState<Membership[]>([]);
  const [consent, setConsent] = useState<Record<string, boolean>>({});
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [ending, setEnding] = useState<string | null>(null);
  const reload = useCallback(async () => {
    try {
      const [invites, connections] = await Promise.all([
        request<Invitation[]>('GET', '/invitations'),
        request<Membership[]>('GET', '/instructor-memberships'),
      ]);
      setInvitations(invites);
      setMemberships(connections);
      setError('');
    } catch {
      setError('Teacher connections could not be loaded. Please retry.');
    }
  }, [request]);
  useEffect(() => {
    void reload();
  }, [reload]);

  const act = async (action: 'accept' | 'decline' | 'end', id: string) => {
    setBusy(true);
    setError('');
    try {
      const token = new URLSearchParams(window.location.hash.slice(1)).get('token');
      const requested = new URLSearchParams(window.location.search).get('invitation');
      await request(
        action === 'end' ? 'DELETE' : 'POST',
        action === 'end' ? `/instructor-memberships/${id}` : `/invitations/${id}/${action}`,
        action === 'accept'
          ? { consent: true, ...(requested === id && token ? { token } : {}) }
          : undefined,
      );
      if (requested === id) window.history.replaceState(null, '', window.location.pathname);
      setEnding(null);
      await reload();
      onChange();
    } catch {
      setError(
        'The request could not be completed. The invitation may have expired or the relationship changed.',
      );
    } finally {
      setBusy(false);
    }
  };

  return (
    <Box sx={{ mb: 3 }} data-testid="teacher-connections">
      {error && (
        <Alert severity="error" action={<Button onClick={() => void reload()}>Retry</Button>}>
          {error}
        </Alert>
      )}
      {invitations.map((invitation) => (
        <Box
          key={invitation.id}
          className="student-assignment-card"
          sx={{ mb: 2, display: 'block' }}
        >
          <Typography component="h2" variant="h6">
            Invitation from {invitation.instructorName}
          </Typography>
          {invitation.message && <Typography sx={{ my: 1 }}>{invitation.message}</Typography>}
          <Typography sx={{ my: 1 }}>
            Accepting shares your name, email, roadmap position, assignments, and practice activity
            from today onward with this teacher. Payment details and earlier practice sessions are
            not shared. You can end access at any time.
          </Typography>
          <FormControlLabel
            control={
              <Checkbox
                checked={Boolean(consent[invitation.id])}
                onChange={(event) =>
                  setConsent({ ...consent, [invitation.id]: event.target.checked })
                }
              />
            }
            label="I agree to share this learning information with this teacher."
          />
          <Box sx={{ display: 'flex', gap: 2, mt: 1 }}>
            <Button
              variant="contained"
              disabled={busy || !consent[invitation.id]}
              onClick={() => void act('accept', invitation.id)}
            >
              Accept invitation
            </Button>
            <Button disabled={busy} onClick={() => void act('decline', invitation.id)}>
              Decline
            </Button>
          </Box>
        </Box>
      ))}
      {memberships.map((membership) => (
        <Box key={membership.id} sx={{ my: 1 }}>
          <Typography component="span">Connected to {membership.instructorName}</Typography>
          {ending === membership.id ? (
            <Alert severity="warning" sx={{ mt: 1 }}>
              End this teacher’s access and cancel your open assignments? Your practice history is
              kept.
              <Button disabled={busy} onClick={() => void act('end', membership.id)}>
                Confirm end access
              </Button>
              <Button onClick={() => setEnding(null)}>Keep connection</Button>
            </Alert>
          ) : (
            <Button disabled={busy} onClick={() => setEnding(membership.id)}>
              End teacher access
            </Button>
          )}
        </Box>
      ))}
    </Box>
  );
}
